import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  MessageSquare, Users, Send, ChevronDown, Lightbulb, Building2,
  FileText, CheckCircle, XCircle, Mail, RefreshCw, Inbox, Phone,
  Pencil, Plus, X, Save, Trash2, Hash, Reply, Check, CheckCheck, RotateCcw,
  FileSignature, Upload, Loader2, Archive, ArchiveRestore
} from 'lucide-react';

import SignatureCanvas from 'react-signature-canvas';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/comms`;
const BIO_API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VALID_TABS = ['org', 'message', 'suggestions', 'announcements', 'benefits', 'rrsp'];

export default function CompanyHub() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState(() => {
    const t = searchParams.get('tab');
    return t && VALID_TABS.includes(t) ? t : 'org';
  });

  useEffect(() => {
    const t = searchParams.get('tab');
    if (t && VALID_TABS.includes(t)) setTab(t);
  }, [searchParams]);
  const canManageSuggestions = ['super_admin', 'admin', 'shop_foreman', 'project_manager', 'project_manager_estimator', 'office_admin', 'fabrication_manager', 'dispatch'].includes(user?.role);
  const isAdmin = ['super_admin', 'admin'].includes(user?.role);
  const canPostAnnouncement = ['super_admin', 'admin', 'hr', 'office_admin', 'project_manager', 'project_manager_estimator', 'fabrication_manager', 'shop_foreman', 'millwright_foreman'].includes(user?.role);

  const tabs = [
    { id: 'org', label: 'Org Chart', icon: Building2 },
    { id: 'message', label: 'Messages', icon: MessageSquare },
    { id: 'suggestions', label: 'Suggestion Box', icon: Lightbulb },
    { id: 'announcements', label: 'Announcements', icon: FileText },
    { id: 'benefits', label: 'Benefits', icon: FileText },
    { id: 'rrsp', label: 'RRSP', icon: FileText },
  ];

  return (
    <div className="space-y-4" data-testid="company-hub">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Company Hub</h1>
        <p className="text-sm text-slate-500 mt-0.5">Org chart, messaging, suggestions</p>
      </div>
      <div className="flex gap-1 bg-white p-1 rounded-xl border border-slate-200 overflow-x-auto">
        {tabs.map(t => (
          <button key={t.id} onClick={() => { setTab(t.id); setSearchParams({ tab: t.id }); }}
            className={`flex items-center gap-1.5 px-2.5 sm:px-4 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm font-medium whitespace-nowrap transition-all flex-shrink-0 ${
              tab === t.id ? 'bg-blue-600 text-white shadow-lg' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'
            }`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>
      {tab === 'org' && <OrgChart />}
      {tab === 'message' && <MessagesTab />}
      {tab === 'suggestions' && <SuggestionsTab isMgmt={canManageSuggestions} />}
      {tab === 'announcements' && <AnnouncementsTab isAdmin={isAdmin} canPost={canPostAnnouncement} />}
      {tab === 'benefits' && <DocumentTab title="Benefits Information" docKey="benefits" isAdmin={isAdmin} />}
      {tab === 'rrsp' && <DocumentTab title="RRSP Information" docKey="rrsp" isAdmin={isAdmin} />}
    </div>
  );
}

/* ── Org Chart ──────────────────────────────────────────────────── */
// Editable person row (module-level so inputs keep focus while typing)
function OrgPersonEditor({ person, onChange, employees, testidPrefix }) {
  const jobTitle = employees.find(e => e.id === person.user_id)?.job_title || '';
  return (
    <div className="space-y-1.5">
      <select value={person.user_id || ''} onChange={(e) => onChange({ user_id: e.target.value })}
        data-testid={`${testidPrefix}-select`}
        className="w-full p-2 bg-white border border-slate-300 rounded text-sm text-slate-800">
        <option value="">— Select employee —</option>
        {employees.map(e => (
          <option key={e.id} value={e.id}>{e.full_name}{e.job_title ? ` — ${e.job_title}` : ''}</option>
        ))}
      </select>
      <div className="grid grid-cols-2 gap-1.5">
        <input value={person.title_override || ''} onChange={(e) => onChange({ title_override: e.target.value })}
          placeholder={jobTitle || 'Title (optional)'} data-testid={`${testidPrefix}-title`}
          className="w-full p-1.5 bg-white border border-slate-300 rounded text-xs" />
        <input value={person.note || ''} onChange={(e) => onChange({ note: e.target.value })}
          placeholder="Note (optional)" data-testid={`${testidPrefix}-note`}
          className="w-full p-1.5 bg-white border border-slate-300 rounded text-xs" />
      </div>
    </div>
  );
}

function OrgChartEditor({ draft, setDraft, employees, onSave, onCancel, saving }) {
  const updateDivision = (i, patch) => setDraft(d => d.map((dv, idx) => idx === i ? { ...dv, ...patch } : dv));
  const updateManager = (i, patch) => setDraft(d => d.map((dv, idx) => idx === i ? { ...dv, manager: { ...dv.manager, ...patch } } : dv));
  const updateReport = (i, j, patch) => setDraft(d => d.map((dv, idx) => idx === i ? { ...dv, reports: dv.reports.map((rp, rj) => rj === j ? { ...rp, ...patch } : rp) } : dv));
  const addReport = (i) => setDraft(d => d.map((dv, idx) => idx === i ? { ...dv, reports: [...dv.reports, { user_id: '', title_override: '', note: '' }] } : dv));
  const removeReport = (i, j) => setDraft(d => d.map((dv, idx) => idx === i ? { ...dv, reports: dv.reports.filter((_, rj) => rj !== j) } : dv));
  const addDivision = () => setDraft(d => [...d, { id: '', name: 'New Division', manager: { user_id: '', title_override: '', note: '' }, reports: [] }]);
  const removeDivision = (i) => setDraft(d => d.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-4" data-testid="org-chart-editor">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-slate-900">Edit Org Chart</h2>
          <p className="text-xs text-slate-500">Pick people from your employee list — contact info stays auto-synced.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={onCancel} data-testid="org-chart-cancel-btn"
            className="px-3 py-1.5 bg-slate-200 text-slate-700 rounded-lg text-sm">Cancel</button>
          <button onClick={onSave} disabled={saving} data-testid="org-chart-save-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium disabled:opacity-50">
            <Save className="w-4 h-4" /> {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {draft.map((div, i) => (
          <div key={i} className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 space-y-3">
            <div className="flex items-center gap-2">
              <input value={div.name} onChange={(e) => updateDivision(i, { name: e.target.value })}
                data-testid={`division-name-${i}`}
                className="flex-1 p-2 bg-slate-50 border border-slate-300 rounded text-sm font-bold text-blue-700" />
              <button onClick={() => removeDivision(i)} data-testid={`division-remove-${i}`} title="Remove division"
                className="p-1.5 text-red-500 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
            </div>
            <div>
              <p className="text-[10px] uppercase text-slate-500 font-semibold mb-1">Manager</p>
              <OrgPersonEditor person={div.manager} onChange={(patch) => updateManager(i, patch)} employees={employees} testidPrefix={`manager-${i}`} />
            </div>
            <div>
              <p className="text-[10px] uppercase text-slate-500 font-semibold mb-1">Reports</p>
              <div className="space-y-2">
                {div.reports.map((rp, j) => (
                  <div key={j} className="flex items-start gap-1.5 bg-slate-50 rounded p-1.5">
                    <div className="flex-1"><OrgPersonEditor person={rp} onChange={(patch) => updateReport(i, j, patch)} employees={employees} testidPrefix={`report-${i}-${j}`} /></div>
                    <button onClick={() => removeReport(i, j)} data-testid={`report-remove-${i}-${j}`}
                      className="p-1 text-red-500 hover:bg-red-100 rounded mt-0.5"><X className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
                <button onClick={() => addReport(i)} data-testid={`add-report-${i}`}
                  className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium">
                  <Plus className="w-3.5 h-3.5" /> Add report
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
      <button onClick={addDivision} data-testid="org-chart-add-division-btn"
        className="flex items-center gap-1.5 px-4 py-2 bg-blue-50 border border-blue-200 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-100">
        <Plus className="w-4 h-4" /> Add Division
      </button>
    </div>
  );
}

function OrgChart() {
  const [org, setOrg] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => { axios.get(`${API}/org-chart`).then(r => setOrg(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const startEdit = async () => {
    try { const r = await axios.get(`${API}/org-chart/employees`); setEmployees(r.data); } catch { setEmployees([]); }
    setDraft((org.divisions || []).map(d => ({
      id: d.id || '',
      name: d.name || '',
      manager: { user_id: d.manager?.user_id || '', title_override: d.manager?.title_override || '', note: d.manager?.note || '' },
      reports: (d.reports || []).map(r => ({ user_id: r.user_id || '', title_override: r.title_override || '', note: r.note || '' })),
    })));
    setEditing(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/org-chart`, { company: org.company, divisions: draft });
      toast.success('Org chart updated');
      setEditing(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  const resetToDefault = async () => {
    if (!window.confirm('Reset the org chart to the default layout? This replaces the current structure (contact info stays auto-synced).')) return;
    try {
      await axios.post(`${API}/org-chart/reset`);
      toast.success('Org chart reset to the default layout');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Reset failed'); }
  };

  if (!org) return <div className="text-center py-8 text-slate-500">Loading...</div>;

  if (editing) {
    return <OrgChartEditor draft={draft} setDraft={setDraft} employees={employees} onSave={save} onCancel={() => setEditing(false)} saving={saving} />;
  }

  const PersonCard = ({ person, isManager }) => (
    <div className={`rounded-lg p-3 ${isManager ? 'bg-blue-50 border border-blue-200' : 'bg-white/50 border border-slate-200'}`}>
      <div>
        <p className={`font-semibold ${isManager ? 'text-slate-900' : 'text-slate-800'} text-sm`}>{person.name}</p>
        <p className="text-xs text-blue-600">{person.title}</p>
        {person.note && <p className="text-[10px] text-amber-600 italic">{person.note}</p>}
      </div>
      <div className="mt-2 space-y-0.5">
        {person.email && (
          <a href={`mailto:${person.email}`} className="text-xs text-blue-500 hover:text-blue-700 hover:underline flex items-center gap-1">
            <Mail className="w-3 h-3" />{person.email}
          </a>
        )}
        {person.phone && (
          <a href={`tel:${person.phone}`} className="text-xs text-slate-600 hover:text-slate-900 flex items-center gap-1">
            <Phone className="w-3 h-3" />{person.phone}
          </a>
        )}
        {person.extension && (
          <span className="text-xs text-slate-600 flex items-center gap-1" data-testid="org-person-extension">
            <Hash className="w-3 h-3" />Ext. {person.extension}
          </span>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-slate-900">{org.company}</h2>
          <p className="text-xs text-slate-500">{org.updated_by && org.updated_by !== 'system' ? `Last updated by ${org.updated_by}` : 'Contact info auto-synced from User Management'}</p>
        </div>
        {org.can_edit && (
          <div className="flex items-center gap-2">
            <button onClick={resetToDefault} data-testid="org-chart-reset-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-900/5 hover:bg-slate-900/10 text-slate-900 border border-slate-200 rounded-lg text-sm font-medium">
              <RotateCcw className="w-4 h-4" /> Reset to Default
            </button>
            <button onClick={startEdit} data-testid="org-chart-edit-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium">
              <Pencil className="w-4 h-4" /> Edit
            </button>
          </div>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {org.divisions.map(div => (
          <div key={div.id || div.name} className="bg-white/70 backdrop-blur-sm rounded-xl border border-slate-200 shadow-sm p-4">
            <h3 className="text-sm font-bold text-blue-700 mb-3 pb-2 border-b border-slate-200">{div.name}</h3>
            {div.manager && <PersonCard person={div.manager} isManager={true} />}
            {div.reports && div.reports.length > 0 && (
              <div className="space-y-2 mt-3 ml-3 border-l-2 border-blue-200 pl-3">
                {div.reports.map((r, ri) => (
                  <PersonCard key={r.user_id || r.name || ri} person={r} isManager={false} />
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Messages Tab ───────────────────────────────────────────────── */
function MessagesTab() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [view, setView] = useState('inbox');
  const [inbox, setInbox] = useState([]);
  const [archived, setArchived] = useState([]);
  const [sent, setSent] = useState([]);
  const [managers, setManagers] = useState([]);
  const [showCompose, setShowCompose] = useState(false);
  const [toId, setToId] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [unread, setUnread] = useState(0);

  const load = useCallback(async () => {
    const [inR, arcR, sentR, mgrR, unR] = await Promise.all([
      axios.get(`${API}/messages/inbox`),
      axios.get(`${API}/messages/inbox?archived=true`),
      axios.get(`${API}/messages/sent`),
      axios.get(`${API}/management-list`),
      axios.get(`${API}/messages/unread-count`),
    ]);
    setInbox(inR.data); setArchived(arcR.data); setSent(sentR.data); setManagers(mgrR.data); setUnread(unR.data.count);
  }, []);

  useEffect(() => { load(); }, [load]);

  const sendMsg = async () => {
    if (!toId || !message) { toast.error('Select recipient and write a message'); return; }
    const toName = replyTo ? replyTo.name : (managers.find(m => m.id === toId)?.full_name || '');
    setSending(true);
    try {
      await axios.post(`${API}/messages/send`, { to_id: toId, to_name: toName, subject, message });
      toast.success('Message sent');
      setShowCompose(false); setToId(''); setSubject(''); setMessage(''); setReplyTo(null);
      load();
    } catch { toast.error('Failed'); }
    setSending(false);
  };

  const startReply = (m) => {
    setReplyTo({ id: m.from_id, name: m.from_name });
    setToId(m.from_id);
    setSubject(m.subject ? (m.subject.startsWith('Re:') ? m.subject : `Re: ${m.subject}`) : 'Re:');
    setMessage('');
    setShowCompose(true);
  };

  const markRead = async (id) => {
    await axios.put(`${API}/messages/${id}/read`);
    load();
  };

  const acknowledgeMsg = async (id) => {
    await axios.put(`${API}/messages/${id}/acknowledge`);
    load();
  };

  const archiveMsg = async (id) => {
    await axios.put(`${API}/messages/${id}/archive`);
    load();
  };

  const unarchiveMsg = async (id) => {
    await axios.put(`${API}/messages/${id}/unarchive`);
    load();
  };

  const archiveAll = async () => {
    if (!window.confirm(`Archive all ${inbox.length} inbox messages?\n\nThey move to the Archived tab (not deleted) and the inbox starts fresh.`)) return;
    const r = await axios.post(`${API}/messages/archive-all`, {});
    toast.success(`${r.data.archived} message${r.data.archived !== 1 ? 's' : ''} archived`);
    load();
  };

  const fmtDate = (iso) => new Date(iso).toLocaleDateString('en-US', {month:'short', day:'numeric', hour:'numeric', minute:'2-digit', timeZone:'America/Toronto'});

  return (
    <div className="space-y-4 max-w-3xl">
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          <button onClick={() => setView('inbox')} className={`px-3 py-1.5 rounded-lg text-sm ${view === 'inbox' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-500'}`}>
            Inbox {unread > 0 && <span className="ml-1 px-1.5 py-0.5 bg-red-500 text-white text-[10px] rounded-full">{unread}</span>}
          </button>
          <button onClick={() => setView('archived')} data-testid="messages-archived-tab" className={`px-3 py-1.5 rounded-lg text-sm ${view === 'archived' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-500'}`}>
            Archived {archived.length > 0 && <span className="ml-1 text-[10px] text-slate-700">({archived.length})</span>}
          </button>
          <button onClick={() => setView('sent')} className={`px-3 py-1.5 rounded-lg text-sm ${view === 'sent' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-500'}`}>Sent</button>
        </div>
        <div className="flex gap-2">
          {view === 'inbox' && inbox.length > 0 && (
            <button onClick={archiveAll} data-testid="messages-archive-all-btn"
              className="px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-800 rounded-lg text-sm flex items-center gap-2">
              <Archive className="w-4 h-4" /> Archive All
            </button>
          )}
          <button onClick={() => { setReplyTo(null); setToId(''); setSubject(''); setShowCompose(!showCompose); }} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium flex items-center gap-2">
            <Send className="w-4 h-4" /> New Message
          </button>
        </div>
      </div>

      {showCompose && (
        <div className="bg-white rounded-xl border border-blue-200 p-4 space-y-3">
          {replyTo ? (
            <div className="flex items-center justify-between bg-slate-100 rounded-lg px-3 py-2" data-testid="reply-recipient-banner">
              <span className="text-sm text-slate-900">Replying to <span className="font-semibold">{replyTo.name}</span></span>
              <button onClick={() => { setReplyTo(null); setToId(''); }} className="text-xs text-slate-500 hover:text-slate-900">Change</button>
            </div>
          ) : (
            <select value={toId} onChange={e => setToId(e.target.value)}
              className="w-full px-3 py-2 bg-white/90 border border-slate-300 rounded-lg text-slate-900 text-sm outline-none">
              <option value="">Select recipient...</option>
              {managers.map(m => <option key={m.id} value={m.id}>{m.full_name} ({m.role})</option>)}
            </select>
          )}
          <input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Subject (optional)"
            className="w-full px-3 py-2 bg-white/90 border border-slate-300 rounded-lg text-slate-900 placeholder-slate-400 text-sm outline-none" />
          <textarea value={message} onChange={e => setMessage(e.target.value)} placeholder="Your message..." rows={4}
            className="w-full px-3 py-2 bg-white/90 border border-slate-300 rounded-lg text-slate-900 placeholder-slate-400 text-sm outline-none resize-none" />
          <div className="flex gap-2">
            <button onClick={sendMsg} disabled={sending} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium">
              {sending ? 'Sending...' : 'Send'}
            </button>
            <button onClick={() => { setShowCompose(false); setReplyTo(null); }} className="px-4 py-2 bg-slate-100 text-slate-900 rounded-lg text-sm">Cancel</button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {(view === 'inbox' ? inbox : view === 'archived' ? archived : sent).length === 0 ? (
          <p className="text-center py-8 text-slate-500">{view === 'archived' ? 'Nothing archived' : 'No messages'}</p>
        ) : (view === 'inbox' ? inbox : view === 'archived' ? archived : sent).map(m => (
          <div key={m.id} onClick={() => !m.read && view === 'inbox' && markRead(m.id)}
            data-testid={`message-row-${m.id}`}
            className={`bg-white rounded-lg border p-3 ${!m.read && view === 'inbox' ? 'border-blue-200 bg-blue-50 cursor-pointer' : 'border-slate-200'} ${view === 'archived' ? 'opacity-75' : ''}`}>
            <div className="flex items-center justify-between">
              <div>
                <span className="text-slate-900 font-medium text-sm">{view === 'sent' ? m.to_name : m.from_name}</span>
                {m.subject && <span className="text-slate-500 text-sm ml-2">— {m.subject}</span>}
                {!m.read && view === 'inbox' && <span className="ml-2 w-2 h-2 bg-blue-500 rounded-full inline-block"></span>}
                {m.acknowledged && view !== 'sent' && (
                  <span data-testid={`message-acked-${m.id}`} className="ml-2 text-[10px] text-emerald-700 inline-flex items-center gap-0.5 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5">
                    <CheckCheck className="w-3 h-3" /> Acknowledged
                  </span>
                )}
              </div>
              <span className="text-xs text-slate-500">{fmtDate(m.created_at)}</span>
            </div>
            <p className="text-xs text-slate-700 mt-1 whitespace-pre-wrap">{m.message}</p>
            {m.link && (
              <button onClick={(e) => { e.stopPropagation(); if (!m.read && view === 'inbox') markRead(m.id); navigate(m.link); }}
                data-testid={`message-open-link-${m.id}`}
                className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-amber-700 hover:text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                {m.type === 'kit_changed' ? 'Open the box' : 'Open'} →
              </button>
            )}
            <div className="flex items-center justify-between mt-2">
              {view === 'inbox' ? (
                <div className="flex items-center gap-3">
                  <button onClick={(e) => { e.stopPropagation(); startReply(m); }}
                    data-testid={`message-reply-${m.id}`}
                    className="text-xs text-blue-700 hover:text-blue-700 flex items-center gap-1 font-medium">
                    <Reply className="w-3 h-3" /> Reply
                  </button>
                  {!m.acknowledged && (
                    <button onClick={(e) => { e.stopPropagation(); acknowledgeMsg(m.id); }}
                      data-testid={`message-ack-${m.id}`}
                      className="text-xs text-emerald-700 hover:text-emerald-700 flex items-center gap-1 font-medium">
                      <CheckCheck className="w-3 h-3" /> Acknowledge
                    </button>
                  )}
                  <button onClick={(e) => { e.stopPropagation(); archiveMsg(m.id); }}
                    data-testid={`message-archive-${m.id}`}
                    className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1 font-medium">
                    <Archive className="w-3 h-3" /> Archive
                  </button>
                </div>
              ) : view === 'archived' ? (
                <button onClick={(e) => { e.stopPropagation(); unarchiveMsg(m.id); }}
                  data-testid={`message-unarchive-${m.id}`}
                  className="text-xs text-blue-700 hover:text-blue-700 flex items-center gap-1 font-medium">
                  <ArchiveRestore className="w-3 h-3" /> Move back to inbox
                </button>
              ) : <span />}
              {view === 'sent' && (
                m.read
                  ? <span data-testid={`message-read-${m.id}`} className="text-[10px] text-emerald-700 flex items-center gap-1"><CheckCheck className="w-3 h-3" /> Read{m.read_at ? ` · ${fmtDate(m.read_at)}` : ''}</span>
                  : <span data-testid={`message-unread-${m.id}`} className="text-[10px] text-slate-500 flex items-center gap-1"><Check className="w-3 h-3" /> Sent · Unread</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Anonymous Suggestion Box ───────────────────────────────────── */
function SuggestionsTab({ isMgmt }) {
  const [suggestions, setSuggestions] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [category, setCategory] = useState('general');
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    if (isMgmt) {
      const r = await axios.get(`${API}/suggestions/list`);
      setSuggestions(r.data);
    }
  }, [isMgmt]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!newMsg.trim()) { toast.error('Write a suggestion'); return; }
    setSending(true);
    try {
      await axios.post(`${API}/suggestions/create`, { message: newMsg, category });
      toast.success('Suggestion submitted anonymously');
      setNewMsg(''); load();
    } catch { toast.error('Failed'); }
    setSending(false);
  };

  const respond = async (id, status, response) => {
    await axios.put(`${API}/suggestions/${id}`, { status, admin_response: response });
    toast.success('Updated'); load();
  };

  const statusColors = { new: 'bg-blue-50 text-blue-700', reviewed: 'bg-amber-50 text-amber-700', implemented: 'bg-emerald-50 text-emerald-700', dismissed: 'bg-slate-100 text-slate-500' };

  return (
    <div className="space-y-4 max-w-3xl">
      {/* Submit form — everyone can see */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h3 className="text-sm font-semibold text-slate-900 mb-2 flex items-center gap-2">
          <Lightbulb className="w-4 h-4 text-amber-700" /> Anonymous Suggestion Box
        </h3>
        <p className="text-xs text-slate-500 mb-3">Your identity is NOT recorded. Speak freely.</p>
        <select value={category} onChange={e => setCategory(e.target.value)}
          className="w-full px-3 py-2 bg-white/90 border border-slate-300 rounded-lg text-slate-900 text-sm outline-none mb-2">
          <option value="general">General Improvement</option>
          <option value="safety">Safety Concern</option>
          <option value="process">Process Improvement</option>
          <option value="morale">Workplace / Morale</option>
          <option value="equipment">Equipment / Tools</option>
        </select>
        <textarea value={newMsg} onChange={e => setNewMsg(e.target.value)} placeholder="Your suggestion..." rows={3}
          className="w-full px-3 py-2 bg-white/90 border border-slate-300 rounded-lg text-slate-900 placeholder-slate-400 text-sm outline-none resize-none mb-2" />
        <button onClick={submit} disabled={sending} className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-sm font-medium">
          {sending ? 'Submitting...' : 'Submit Anonymously'}
        </button>
      </div>

      {/* Management view of suggestions */}
      {isMgmt && suggestions.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm font-medium text-slate-700">Submitted Suggestions</h3>
          {suggestions.map(s => (
            <div key={s.id} className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full capitalize ${statusColors[s.status]}`}>{s.status}</span>
                    <span className="text-[10px] text-slate-500 capitalize">{s.category}</span>
                    <span className="text-[10px] text-slate-600">{new Date(s.created_at).toLocaleDateString()}</span>
                  </div>
                  <p className="text-sm text-slate-900">{s.message}</p>
                  {s.admin_response && <p className="text-xs text-emerald-700 mt-1">Response: {s.admin_response}</p>}
                </div>
                <div className="flex gap-1 ml-2">
                  <button onClick={() => respond(s.id, 'reviewed', '')} className="px-2 py-1 bg-amber-600 text-white rounded text-[10px]">Review</button>
                  <button onClick={() => { const r = prompt('Response:'); if (r) respond(s.id, 'implemented', r); }} className="px-2 py-1 bg-emerald-600 text-white rounded text-[10px]">Implement</button>
                  <button onClick={() => respond(s.id, 'dismissed', '')} className="px-2 py-1 bg-slate-200 text-slate-900 rounded text-[10px]">Dismiss</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Announcements Tab ──────────────────────────────────────────── */
function AnnouncementsTab({ isAdmin, canPost }) {
  const { user } = useAuth();
  const [announcements, setAnnouncements] = useState([]);
  const [pending, setPending] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showWaiver, setShowWaiver] = useState(false);
  const [statusModal, setStatusModal] = useState(null); // { ann, acknowledged, not_acknowledged, ack_count, total }
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [priority, setPriority] = useState('normal');
  const [mandatory, setMandatory] = useState(true);
  const [action, setAction] = useState('');          // '' | 'enable_push' | 'link'
  const [actionUrl, setActionUrl] = useState('');
  const [actionLabel, setActionLabel] = useState('');
  const [ackAnnId, setAckAnnId] = useState(null);
  const [ackMethod, setAckMethod] = useState('click');
  const sigRef = React.useRef(null);

  const load = useCallback(async () => {
    const [allR, pendR] = await Promise.all([
      axios.get(`${BIO_API}/announcements/list`),
      axios.get(`${BIO_API}/announcements/pending`),
    ]);
    setAnnouncements(allR.data);
    setPending(pendR.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!title || !message) { toast.error('Title and message required'); return; }
    if (action === 'link' && !actionUrl.trim()) { toast.error('Enter the link for the button'); return; }
    await axios.post(`${BIO_API}/announcements/create`, {
      title, message, priority, mandatory,
      action: action || null, action_url: action === 'link' ? actionUrl.trim() : null, action_label: action === 'link' ? actionLabel.trim() : null,
    });
    toast.success(mandatory ? 'Announcement posted — everyone must sign off' : 'Announcement posted');
    setShowCreate(false); setTitle(''); setMessage(''); setPriority('normal'); setMandatory(true); setAction(''); setActionUrl(''); setActionLabel('');
    load();
  };

  const deleteAnnouncement = async (annId) => {
    if (!window.confirm('Clear this announcement? This removes it for everyone.')) return;
    try {
      await axios.delete(`${BIO_API}/announcements/${annId}`);
      toast.success('Announcement cleared');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to clear announcement');
    }
  };

  const clearAllAnnouncements = async () => {
    if (announcements.length === 0) return;
    if (!window.confirm(`Clear ALL ${announcements.length} announcement(s)? This removes them for everyone.`)) return;
    try {
      await Promise.all(announcements.map(a => axios.delete(`${BIO_API}/announcements/${a.id}`)));
      toast.success('All announcements cleared');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to clear announcements');
    }
  };

  const acknowledge = async (annId, method, sigData) => {
    await axios.post(`${BIO_API}/announcements/${annId}/acknowledge`, {
      method, signature_data: sigData || null,
    });
    toast.success('Acknowledged');
    setAckAnnId(null);
    load();
  };

  const biometricAck = async (annId) => {
    if (!window.PublicKeyCredential) { toast.error('Biometric not supported'); return; }
    try {
      // Use the biometric to prove identity
      const optR = await axios.post(`${BIO_API}/biometric/login-options`, { email: user.email });
      const opts = optR.data;
      const toBuffer = (b64) => {
        const pad = b64.length % 4 === 0 ? '' : '='.repeat(4 - (b64.length % 4));
        const b = atob(b64.replace(/-/g, '+').replace(/_/g, '/') + pad);
        return Uint8Array.from(b, c => c.charCodeAt(0)).buffer;
      };
      await navigator.credentials.get({
        publicKey: {
          challenge: toBuffer(opts.challenge),
          rpId: opts.rpId,
          allowCredentials: opts.allowCredentials.map(c => ({ ...c, id: toBuffer(c.id) })),
          userVerification: 'required',
          timeout: 60000,
        },
      });
      await acknowledge(annId, 'biometric', null);
    } catch (e) {
      if (e.response?.data?.detail) toast.error(e.response.data.detail);
      else if (e.name !== 'NotAllowedError') toast.error('Biometric verification failed');
    }
  };

  const priorityColors = { normal: 'border-slate-200', important: 'border-amber-200 bg-amber-50', critical: 'border-red-200 bg-red-50' };

  return (
    <div className="space-y-4 max-w-3xl">
      {/* Pending announcements requiring sign-off */}
      {pending.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <p className="text-sm text-red-700 font-bold mb-2">You have {pending.length} announcement(s) requiring your sign-off</p>
          {pending.map(ann => (
            <div key={ann.id} className="bg-white rounded-lg border border-red-200 p-4 mb-2">
              <h4 className="text-slate-900 font-bold">{ann.title}</h4>
              <p className="text-sm text-slate-700 mt-1 whitespace-pre-wrap">{ann.message}</p>
              <p className="text-xs text-slate-500 mt-2">Posted by {ann.created_by} on {new Date(ann.created_at).toLocaleDateString()}</p>

              {ackAnnId === ann.id ? (
                <div className="mt-3 space-y-2">
                  <div className="flex gap-2">
                    <button onClick={() => setAckMethod('click')} className={`px-3 py-1 rounded text-xs ${ackMethod === 'click' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-500'}`}>Click to Acknowledge</button>
                    <button onClick={() => setAckMethod('signature')} className={`px-3 py-1 rounded text-xs ${ackMethod === 'signature' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-500'}`}>Sign</button>
                    {window.PublicKeyCredential && <button onClick={() => setAckMethod('biometric')} className={`px-3 py-1 rounded text-xs ${ackMethod === 'biometric' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-500'}`}>Biometric</button>}
                  </div>

                  {ackMethod === 'click' && (
                    <button onClick={() => acknowledge(ann.id, 'click')} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium">
                      I Acknowledge This Announcement
                    </button>
                  )}

                  {ackMethod === 'signature' && (
                    <div>
                      <div className="bg-white rounded-lg" style={{width:'100%',maxWidth:'400px',height:'120px'}}>
                        <SignatureCanvas ref={sigRef} penColor="black"
                          canvasProps={{width:400, height:120, style:{width:'100%',height:'120px',borderRadius:'8px'}}} />
                      </div>
                      <div className="flex gap-2 mt-2">
                        <button onClick={() => {
                          if (sigRef.current && !sigRef.current.isEmpty()) {
                            acknowledge(ann.id, 'signature', sigRef.current.toDataURL('image/png'));
                          } else toast.error('Please sign first');
                        }} className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium">Submit Signature</button>
                        <button onClick={() => sigRef.current?.clear()} className="px-3 py-2 bg-slate-100 text-slate-900 rounded-lg text-sm">Clear</button>
                      </div>
                    </div>
                  )}

                  {ackMethod === 'biometric' && (
                    <button onClick={() => biometricAck(ann.id)} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium flex items-center gap-2">
                      Verify with Fingerprint / Face ID
                    </button>
                  )}
                </div>
              ) : (
                <button onClick={() => setAckAnnId(ann.id)} className="mt-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium">
                  Acknowledge
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Admin/HR/Manager: create announcement */}
      {canPost && (
        <div className="flex justify-end gap-2">
          <button onClick={() => { setShowWaiver(!showWaiver); setShowCreate(false); }} data-testid="new-waiver-btn" className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-sm font-medium flex items-center gap-1.5">
            <FileSignature className="w-4 h-4" /> New Waiver
          </button>
          <button onClick={() => { setShowCreate(!showCreate); setShowWaiver(false); }} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium">+ New Announcement</button>
        </div>
      )}
      {showWaiver && (
        <WaiverCreator onPosted={() => { setShowWaiver(false); load(); }} onCancel={() => setShowWaiver(false)} />
      )}
      {showCreate && (
        <div className="bg-white rounded-xl border border-blue-200 p-4 space-y-2">
          <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Title"
            className="w-full px-3 py-2 bg-white/90 border border-slate-300 rounded-lg text-slate-900 placeholder-slate-400 text-sm outline-none" />
          <textarea value={message} onChange={e => setMessage(e.target.value)} placeholder="Announcement message..." rows={4}
            className="w-full px-3 py-2 bg-white/90 border border-slate-300 rounded-lg text-slate-900 placeholder-slate-400 text-sm outline-none resize-none" />
          <select value={priority} onChange={e => setPriority(e.target.value)}
            className="w-full px-3 py-2 bg-white/90 border border-slate-300 rounded-lg text-slate-900 text-sm outline-none">
            <option value="normal">Normal</option>
            <option value="important">Important</option>
            <option value="critical">Critical</option>
          </select>
          <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer select-none">
            <input type="checkbox" checked={mandatory} onChange={e => setMandatory(e.target.checked)}
              className="w-4 h-4 rounded" data-testid="ann-mandatory-toggle" />
            Require sign-off (blocks the app for everyone until each person signs)
          </label>
          <div className="pt-1">
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Action button (optional)</label>
            <select value={action} onChange={e => setAction(e.target.value)} data-testid="ann-action-select"
              className="w-full px-3 py-2 bg-white/90 border border-slate-300 rounded-lg text-slate-900 text-sm outline-none">
              <option value="">None</option>
              <option value="enable_push">🔔 Turn on push notifications (inline — one tap, right on the announcement)</option>
              <option value="link">🔗 Link button (to a page in 365Ops or a website)</option>
            </select>
            {action === 'link' && (
              <div className="grid sm:grid-cols-2 gap-2 mt-2">
                <input value={actionUrl} onChange={e => setActionUrl(e.target.value)} placeholder="Link, e.g. /notifications or https://…" data-testid="ann-action-url"
                  className="w-full px-3 py-2 bg-white/90 border border-slate-300 rounded-lg text-slate-900 placeholder-slate-400 text-sm outline-none" />
                <input value={actionLabel} onChange={e => setActionLabel(e.target.value)} placeholder="Button label, e.g. Turn on notifications" data-testid="ann-action-label"
                  className="w-full px-3 py-2 bg-white/90 border border-slate-300 rounded-lg text-slate-900 placeholder-slate-400 text-sm outline-none" />
              </div>
            )}
            {action === 'enable_push' && <p className="text-xs text-slate-500 mt-1.5">Everyone sees a "Turn on notifications" button with iPhone / Android instructions built in — no need to leave the announcement.</p>}
          </div>
          <button onClick={create} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium">Post Announcement</button>
        </div>
      )}

      {/* All announcements */}
      <div className="space-y-2">
        {isAdmin && announcements.length > 0 && (
          <div className="flex justify-end">
            <button data-testid="ann-clear-all-btn" onClick={clearAllAnnouncements}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-700 hover:text-white bg-red-50 hover:bg-red-600 border border-red-200 rounded-lg transition-colors">
              <Trash2 className="w-3.5 h-3.5" /> Clear All
            </button>
          </div>
        )}
        {announcements.map(ann => (
          <div key={ann.id} className={`bg-white rounded-xl border p-4 ${priorityColors[ann.priority]}`}>
            <div className="flex items-start justify-between">
              <div>
                <h4 className="text-slate-900 font-bold flex items-center gap-2">
                  {ann.type === 'waiver' && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-500 text-slate-900"><FileSignature className="w-3 h-3" /> WAIVER</span>}
                  {(ann.target_user_ids?.length > 0) && <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold border border-amber-300 text-amber-700">SENT TO {ann.target_user_ids.length} {ann.target_user_ids.length === 1 ? 'PERSON' : 'PEOPLE'}</span>}
                  {ann.type === 'waiver' && ann.onboarding_required && <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 border border-emerald-300 text-emerald-700" data-testid={`ann-onboarding-badge-${ann.id}`}>NEW HIRES SIGN</span>}
                  {ann.title}
                </h4>
                {ann.type === 'waiver' && isAdmin && (
                  <label className="mt-2 inline-flex items-center gap-2 text-xs text-slate-700 cursor-pointer" data-testid={`ann-onboarding-toggle-${ann.id}`}>
                    <input type="checkbox" checked={!!ann.onboarding_required} onChange={async (e) => {
                      try {
                        await axios.put(`${BIO_API}/announcements/${ann.id}/onboarding-required`, { value: e.target.checked });
                        toast.success(e.target.checked ? 'Every new hire will sign this at the end of onboarding' : 'No longer part of onboarding');
                        load();
                      } catch (err) { toast.error(err.response?.data?.detail || 'Could not update'); }
                    }} />
                    Every new hire must sign this during onboarding
                  </label>
                )}
                <p className="text-sm text-slate-700 mt-1">{ann.message}</p>
                <p className="text-xs text-slate-500 mt-2">
                  {ann.created_by} — {new Date(ann.created_at).toLocaleDateString()} — 
                  <span className="text-emerald-700"> {ann.acknowledgments?.length || 0} {ann.type === 'waiver' ? 'signed' : 'acknowledged'}</span>
                </p>
              </div>
              {isAdmin && (
                <div className="flex items-center gap-3 whitespace-nowrap">
                  <button data-testid={`ann-status-${ann.id}`} onClick={async () => {
                    try {
                      const r = await axios.get(`${BIO_API}/announcements/${ann.id}/status`);
                      setStatusModal(r.data);
                    } catch (e) { toast.error('Could not load status'); }
                  }} className="text-xs text-blue-700 hover:text-blue-700">View Status</button>
                  <button data-testid={`ann-delete-${ann.id}`} onClick={() => deleteAnnouncement(ann.id)}
                    className="text-xs text-red-600 hover:text-red-700 flex items-center gap-1" title="Clear announcement">
                    <Trash2 className="w-3.5 h-3.5" /> Clear
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {statusModal && (
        <StatusModal data={statusModal} onClose={() => setStatusModal(null)} />
      )}
    </div>
  );
}


/* ── Announcement / Waiver status modal ─────────────────────────── */
function StatusModal({ data, onClose }) {
  const token = localStorage.getItem('token');
  const ann = data.announcement || {};
  const isWaiver = ann.type === 'waiver';
  // map user_id -> acknowledgment record (for signed PDF links)
  const ackByUser = {};
  (ann.acknowledgments || []).forEach(a => { ackByUser[a.user_id] = a; });

  return (
    <div className="fixed inset-0 z-[100000] bg-slate-950/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-lg bg-white border border-slate-200 rounded-2xl shadow-2xl max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-testid="ann-status-modal">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h3 className="text-slate-900 font-bold">{ann.title || 'Status'}</h3>
            <p className="text-xs text-slate-500 mt-1">{data.ack_count}/{data.total} {isWaiver ? 'signed' : 'acknowledged'}</p>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 overflow-y-auto space-y-4">
          <div>
            <p className="text-xs font-bold text-emerald-700 mb-2 uppercase tracking-wide">Completed ({data.acknowledged.length})</p>
            {data.acknowledged.length === 0 ? <p className="text-sm text-slate-500">No one yet</p> : (
              <div className="space-y-1">
                {data.acknowledged.map(u => {
                  const ack = ackByUser[u.id];
                  return (
                    <div key={u.id} className="flex items-center justify-between text-sm bg-white rounded-lg px-3 py-2">
                      <span className="text-slate-800 flex items-center gap-2"><CheckCircle className="w-4 h-4 text-emerald-700" /> {u.full_name}</span>
                      {isWaiver && ack?.ack_id && (
                        <a href={`${BIO_API}/waivers/signed/${ack.ack_id}/pdf?token=${encodeURIComponent(token)}`} target="_blank" rel="noopener noreferrer"
                          className="text-xs text-blue-700 hover:text-blue-700" data-testid={`signed-pdf-${u.id}`}>View signed PDF</a>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div>
            <p className="text-xs font-bold text-red-600 mb-2 uppercase tracking-wide">Outstanding ({data.not_acknowledged.length})</p>
            {data.not_acknowledged.length === 0 ? <p className="text-sm text-emerald-700">Everyone is done 🎉</p> : (
              <div className="space-y-1">
                {data.not_acknowledged.map(u => (
                  <div key={u.id} className="flex items-center gap-2 text-sm bg-white rounded-lg px-3 py-2 text-slate-700">
                    <XCircle className="w-4 h-4 text-red-600" /> {u.full_name}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


/* ── Waiver creator: upload PDF + place signature line + post ────── */
function WaiverCreator({ onPosted, onCancel }) {
  const token = localStorage.getItem('token');
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [uploading, setUploading] = useState(false);
  const [posting, setPosting] = useState(false);
  const [template, setTemplate] = useState(null); // {id, page_count, pages}
  const [page, setPage] = useState(0);
  const [placement, setPlacement] = useState(null); // {page,x,y,w,h}
  const [requireScroll, setRequireScroll] = useState(true);
  const [requireAgree, setRequireAgree] = useState(true);
  const [audience, setAudience] = useState('all'); // 'all' | 'select' | 'new_hires'
  const [users, setUsers] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [prevTemplates, setPrevTemplates] = useState(null);
  const SIG_W = 0.32, SIG_H = 0.06;

  useEffect(() => {
    axios.get(`${BIO_API}/waivers/templates`).then(r => setPrevTemplates(r.data || [])).catch(() => setPrevTemplates([]));
  }, []);

  useEffect(() => {
    if (audience === 'select' && users.length === 0) {
      axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/users`)
        .then(r => setUsers((r.data.users || []).filter(u => u.is_active).sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''))))
        .catch(() => toast.error('Could not load employee list'));
    }
  }, [audience, users.length]);

  const toggleUser = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const upload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.type !== 'application/pdf') { toast.error('Please choose a PDF file'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${BIO_API}/waivers/templates`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setTemplate(r.data);
      const last = Math.max(0, (r.data.page_count || 1) - 1);
      setPage(last);
      if (!title) setTitle(file.name.replace(/\.pdf$/i, ''));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    }
    setUploading(false);
  };

  const onPageClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    let x = (e.clientX - rect.left) / rect.width - SIG_W / 2;
    let y = (e.clientY - rect.top) / rect.height - SIG_H / 2;
    x = Math.min(Math.max(x, 0), 1 - SIG_W);
    y = Math.min(Math.max(y, 0), 1 - SIG_H);
    setPlacement({ page, x, y, w: SIG_W, h: SIG_H });
  };

  const post = async () => {
    if (!title) { toast.error('Add a title'); return; }
    if (!template) { toast.error('Upload the waiver PDF'); return; }
    if (!placement) { toast.error('Tap on the PDF where the signature should go'); return; }
    if (audience === 'select' && selectedIds.length === 0) { toast.error('Pick at least one person'); return; }
    setPosting(true);
    try {
      await axios.post(`${BIO_API}/announcements/create`, {
        type: 'waiver',
        title,
        message,
        priority: 'important',
        mandatory: true,
        target_roles: [],
        target_user_ids: audience === 'select' ? selectedIds : [],
        waiver_template_id: template.id,
        signature_placement: placement,
        require_scroll: requireScroll,
        require_agree: requireAgree,
        onboarding_only: audience === 'new_hires',
        onboarding_required: audience !== 'select',
      });
      toast.success(audience === 'select'
        ? `Waiver sent to ${selectedIds.length} ${selectedIds.length === 1 ? 'person' : 'people'} — only they will be asked to sign`
        : audience === 'new_hires'
          ? 'Saved — nobody on staff is asked; every new hire signs it as the last onboarding step, permanently'
          : 'Waiver posted — every employee must read and sign it (new hires sign it during onboarding)');
      onPosted();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to post waiver');
    }
    setPosting(false);
  };

  return (
    <div className="bg-white rounded-xl border border-amber-200 p-4 space-y-3" data-testid="waiver-creator">
      <div className="flex items-center gap-2 text-amber-700 font-semibold text-sm">
        <FileSignature className="w-4 h-4" /> New Waiver — employees read &amp; sign
      </div>
      <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Waiver title (e.g. Rental Equipment & Accommodations Damage Policy)"
        data-testid="waiver-title" className="w-full px-3 py-2 bg-white/90 border border-slate-300 rounded-lg text-slate-900 placeholder-slate-400 text-sm outline-none" />
      <textarea value={message} onChange={e => setMessage(e.target.value)} placeholder="Short instructions shown above the document (optional)" rows={2}
        className="w-full px-3 py-2 bg-white/90 border border-slate-300 rounded-lg text-slate-900 placeholder-slate-400 text-sm outline-none resize-none" />

      {!template ? (
        <div className="space-y-2">
          <label className="flex items-center justify-center gap-2 px-4 py-6 border-2 border-dashed border-slate-300 rounded-lg text-slate-700 text-sm cursor-pointer hover:border-amber-500 hover:text-amber-700 transition-colors" data-testid="waiver-upload-label">
            {uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</> : <><Upload className="w-4 h-4" /> Upload the waiver PDF</>}
            <input type="file" accept="application/pdf" onChange={upload} className="hidden" />
          </label>
          {prevTemplates?.length > 0 && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-2" data-testid="waiver-prev-templates">
              <p className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-1">…or reuse a PDF you uploaded before</p>
              <div className="max-h-40 overflow-y-auto space-y-1">
                {prevTemplates.map(t => (
                  <button key={t.id} type="button" onClick={() => { setTemplate(t); setPage(Math.max(0, (t.page_count || 1) - 1)); if (!title) setTitle((t.used_by?.[0]?.title) || (t.filename || '').replace(/\.pdf$/i, '')); }}
                    className="w-full text-left px-2 py-1.5 rounded hover:bg-slate-100 text-sm text-slate-800 flex items-center justify-between gap-2" data-testid={`waiver-prev-${t.id}`}>
                    <span className="truncate">{t.filename} <span className="text-slate-500 text-xs">· {t.page_count} p{t.uploaded_at ? ` · ${new Date(t.uploaded_at).toLocaleDateString()}` : ''}</span></span>
                    {t.used_by?.length > 0 && <span className="text-[10px] text-slate-500 truncate">used for “{t.used_by[0].title}”{t.used_by[0].archived ? ' (deleted)' : ''}</span>}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-xs text-slate-700">
              <span className="font-semibold text-slate-900">{template.filename}</span> · {template.page_count} page(s)
            </p>
            <button onClick={() => { setTemplate(null); setPlacement(null); }} className="text-xs text-slate-500 hover:text-slate-900">Change file</button>
          </div>
          {template.page_count > 1 && (
            <div className="flex items-center gap-2 text-xs text-slate-700">
              <span>Signature is on page:</span>
              <select value={page} onChange={e => setPage(Number(e.target.value))} className="bg-white/90 text-slate-900 rounded px-2 py-1">
                {Array.from({ length: template.page_count }).map((_, i) => <option key={i} value={i}>{i + 1}</option>)}
              </select>
            </div>
          )}
          <p className="text-xs text-amber-700">👆 Tap on the document below where each person's signature should land.</p>
          <div className="relative inline-block max-w-full border border-slate-300 rounded overflow-hidden" style={{ cursor: 'crosshair' }} onClick={onPageClick} data-testid="waiver-place-area">
            <img
              src={`${BIO_API}/waivers/templates/${template.id}/page/${page}?token=${encodeURIComponent(token)}`}
              alt={`Waiver page ${page + 1}`}
              className="block w-full select-none"
              draggable={false}
            />
            {placement && placement.page === page && (
              <div
                className="absolute border-2 border-emerald-500 bg-emerald-50 flex items-center justify-center text-[10px] text-emerald-900 font-bold pointer-events-none"
                style={{ left: `${placement.x * 100}%`, top: `${placement.y * 100}%`, width: `${placement.w * 100}%`, height: `${placement.h * 100}%` }}
              >
                Signature
              </div>
            )}
          </div>
        </div>
      )}

      <div className="flex flex-col gap-2 pt-1">
        <div className="space-y-2">
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wide">Who has to sign?</p>
          <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer select-none">
            <input type="radio" name="waiver-audience" data-testid="waiver-audience-all" checked={audience === 'all'} onChange={() => setAudience('all')} className="w-4 h-4" />
            All active employees <span className="text-xs text-slate-500">(new hires also sign it during onboarding)</span>
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer select-none">
            <input type="radio" name="waiver-audience" data-testid="waiver-audience-new-hires" checked={audience === 'new_hires'} onChange={() => setAudience('new_hires')} className="w-4 h-4" />
            New hires only — permanent onboarding step <span className="text-xs text-slate-500">(nobody on staff is asked; use when everyone already signed)</span>
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer select-none">
            <input type="radio" name="waiver-audience" data-testid="waiver-audience-select" checked={audience === 'select'} onChange={() => setAudience('select')} className="w-4 h-4" />
            Specific people only <span className="text-xs text-slate-500">(e.g. a test send to yourself)</span>
          </label>
          {audience === 'select' && (
            <div className="max-h-44 overflow-y-auto bg-slate-50 border border-slate-200 rounded-lg p-2 space-y-0.5" data-testid="waiver-user-picker">
              {users.length === 0 ? (
                <p className="text-xs text-slate-500 px-1 py-2">Loading employees…</p>
              ) : users.map(u => (
                <label key={u.id} className="flex items-center gap-2 text-sm text-slate-800 cursor-pointer select-none px-1 py-1 rounded hover:bg-slate-50">
                  <input type="checkbox" data-testid={`waiver-user-${u.id}`} checked={selectedIds.includes(u.id)} onChange={() => toggleUser(u.id)} className="w-4 h-4 rounded" />
                  {u.full_name}
                </label>
              ))}
            </div>
          )}
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer select-none">
          <input type="checkbox" checked={requireScroll} onChange={e => setRequireScroll(e.target.checked)} className="w-4 h-4 rounded" />
          Require reading to the end before signing
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer select-none">
          <input type="checkbox" checked={requireAgree} onChange={e => setRequireAgree(e.target.checked)} className="w-4 h-4 rounded" />
          Require "I have read and agree" checkbox
        </label>
      </div>

      <div className="flex gap-2 pt-1">
        <button onClick={post} disabled={posting} data-testid="waiver-post-btn" className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-sm font-bold disabled:opacity-50">
          {posting ? 'Posting…' : (audience === 'select'
            ? `Send Waiver to ${selectedIds.length || ''} ${selectedIds.length === 1 ? 'Person' : 'People'}`.replace('  ', ' ')
            : audience === 'new_hires' ? 'Save as Onboarding Waiver' : 'Post Waiver to All Employees')}
        </button>
        <button onClick={onCancel} className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-900 rounded-lg text-sm">Cancel</button>
      </div>
    </div>
  );
}


/* ── Document Tab (Benefits / RRSP) ─────────────────────────────── */
function DocumentTab({ title, docKey, isAdmin }) {
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    axios.get(`${BIO_API}/documents/${docKey}`).then(r => setDocs(r.data)).catch(() => {});
  }, [docKey]);

  const upload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('doc_type', docKey);
    try {
      await axios.post(`${BIO_API}/documents/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Document uploaded');
      const r = await axios.get(`${BIO_API}/documents/${docKey}`);
      setDocs(r.data);
    } catch (err) { toast.error('Upload failed'); }
    setUploading(false);
  };

  return (
    <div className="space-y-4 max-w-3xl">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-slate-900">{title}</h2>
        {isAdmin && (
          <label className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium cursor-pointer">
            {uploading ? 'Uploading...' : 'Upload Document'}
            <input type="file" accept=".pdf,.png,.jpg,.jpeg,.doc,.docx" onChange={upload} className="hidden" />
          </label>
        )}
      </div>
      {docs.length === 0 ? (
        <div className="text-center py-12 text-slate-500">No documents uploaded yet{isAdmin ? ' — click Upload to add one' : ''}</div>
      ) : (
        <div className="space-y-3">
          {docs.map((doc, i) => (
            <div key={i} className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-900 font-medium">{doc.filename}</p>
                  <p className="text-xs text-slate-500">Uploaded {new Date(doc.uploaded_at).toLocaleDateString()} by {doc.uploaded_by}</p>
                </div>
                <a href={doc.url} target="_blank" rel="noopener noreferrer"
                  className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-900 rounded-lg text-xs">View</a>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
